import { getBaseUrl } from '@databases/database-state';
import { useLocale } from '@databases/intl';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils, useActionWithDependencies } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { type DefaultRootState } from '@databases/redux/default-root-state';
import { getSelectDatabaseDraft } from '@databases/redux/selectors/getSelectDatabaseDraft';
import { getService, type ContainerServices } from '@databases/service-container';
import {
  type Database,
  type DatabaseEntry,
  type DatabaseFieldType,
  DatabaseFieldType as FieldType
} from '@databases/types/database';
import { getConvertedDatabaseValue } from '@databases/utils/database/converted-value-helpers';
import {
  getStringRepresentation,
  isEmptyValue,
  loadDependencies,
  type LoadDependenciesHelpers
} from '@databases/utils/database/field-types';
import { toDisplayDate } from '@databases/utils/database/field-types/date';
import { filterValidPageLinkValue } from '@databases/utils/database/page-link-utils';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue,
  isBrokenDatabase
} from '@databases/utils/database/utils';
import { useCallback, useEffect, useState } from 'react';
import { type IntlShape, useIntl } from 'react-intl-next';

import { type AiSummaryPreviewOption } from './AiSummaryFieldModal.tsx';

type PreviewValue =
  | { kind: 'empty' }
  | {
      kind: 'value';
      text: string;
    };

const EMPTY_PREVIEW_OPTIONS: AiSummaryPreviewOption[] = [];
const MAX_PREVIEW_OPTIONS = 5;
const PREVIEW_FIELD_COUNT = 2;
const selectDatabaseDraft = getSelectDatabaseDraft(false);

type ResolvedPreviewValues = Readonly<Record<string, Readonly<Record<string, string>>>>;

type SmartLinkDataClient = Pick<ContainerServices['SmartLinkClient'], 'fetchData'>;

const getSmartLinkTitle = (
  response: Awaited<ReturnType<SmartLinkDataClient['fetchData']>>
): string | undefined => {
  const data = response.data;

  if (data && typeof data === 'object' && 'name' in data) {
    return typeof data.name === 'string' && data.name.trim() ? data.name : undefined;
  }

  return undefined;
};

const getPageLinkUrl = (contentId: string, baseUrl: string): string =>
  `${baseUrl}/spaces/~/pages/${contentId}`;

const getPreviewEntries = (database: Database): DatabaseEntry[] =>
  database.entries
    .slice(0, MAX_PREVIEW_OPTIONS)
    .map((entryId) => getDatabaseEntryRequired(database, entryId));

const getPreviewFields = (database: Database) =>
  database.fields
    .slice(0, PREVIEW_FIELD_COUNT)
    .map((fieldId) => getDatabaseFieldRequired(database, fieldId));

export const resolvePageLinkPreviewValues = async ({
  baseUrl,
  database,
  smartLinkClient
}: {
  baseUrl: string;
  database: Database;
  smartLinkClient: SmartLinkDataClient;
}): Promise<ResolvedPreviewValues> => {
  const pageLinkFields = getPreviewFields(database).filter(
    (field) => field.type === FieldType.PAGE_LINK
  );

  if (pageLinkFields.length === 0) {
    return {};
  }

  const resolvedValues = await Promise.all(
    getPreviewEntries(database).flatMap((entry) =>
      pageLinkFields.map(async (field) => {
        const value = getDatabaseValue(database, field, entry);

        if (value.type !== FieldType.PAGE_LINK) {
          return undefined;
        }

        const firstPageLink = filterValidPageLinkValue(value.value)[0];
        if (
          !firstPageLink ||
          'contentTitle' in firstPageLink ||
          !('contentId' in firstPageLink)
        ) {
          return undefined;
        }

        const url = getPageLinkUrl(firstPageLink.contentId, baseUrl);

        try {
          const response = await smartLinkClient.fetchData(url);
          return [entry.id, field.id, getSmartLinkTitle(response) ?? url] as const;
        } catch {
          return [entry.id, field.id, url] as const;
        }
      })
    )
  );

  const valuesByEntry: Record<string, Record<string, string>> = {};
  for (const resolvedValue of resolvedValues) {
    if (resolvedValue) {
      const [entryId, fieldId, text] = resolvedValue;
      (valuesByEntry[entryId] ??= {})[fieldId] = text;
    }
  }
  return valuesByEntry;
};

export const getFirstAiSummaryPreviewValue = (
  fieldType: DatabaseFieldType,
  text: string
): string => {
  switch (fieldType) {
    case FieldType.PAGE_LINK:
    case FieldType.ATTACHMENT:
    case FieldType.USER:
    case FieldType.SELECT:
    case FieldType.HYPERLINK:
    case FieldType.JIRA_ISSUE:
    case FieldType.ENTRY_LINK:
    case FieldType.ENTRY_DETAILS:
    case FieldType.BACKLINK:
    case FieldType.PAGE_LABELS:
    case FieldType.JIRA_ISSUE_DETAILS:
      return text.split(/\r?\n/, 1)[0] ?? '';
    case FieldType.TEXT:
    case FieldType.AI_SUMMARY:
    case FieldType.NUMBER:
    case FieldType.DATE:
    case FieldType.CHECKBOX:
    case FieldType.PAGE_DETAILS:
    case FieldType.PAGE_STATUS:
      return text;
    default:
      fieldType satisfies never;
      return '';
  }
};

const serializePreviewValue = ({
  database,
  entry,
  field,
  state,
  locale,
  baseUrl,
  resolvedPreviewValues,
  unnamedEntryLabel,
  checkedLabel,
  uncheckedLabel
}: {
  database: Database;
  entry: DatabaseEntry;
  field: ReturnType<typeof getPreviewFields>[number] | undefined;
  state: DefaultRootState | undefined;
  locale: string;
  baseUrl: string;
  resolvedPreviewValues: ResolvedPreviewValues;
  unnamedEntryLabel: string;
  checkedLabel: string;
  uncheckedLabel: string;
}): PreviewValue => {
  if (!field) {
    return { kind: 'empty' };
  }

  const value = getConvertedDatabaseValue(database, field, entry, state);
  const context = { database, entry, field, value };

  if (isEmptyValue(context, state)) {
    return { kind: 'empty' };
  }

  const text =
    value.type === FieldType.CHECKBOX
      ? value.value
        ? checkedLabel
        : uncheckedLabel
      : value.type === FieldType.DATE
        ? (toDisplayDate(value.value ?? '', locale, field.settings.timeFormat) ??
          value.value ??
          '')
        : (getStringRepresentation({ database, entry, field, value }, state) ?? '');

  const firstTextValue = getFirstAiSummaryPreviewValue(field.type, text);
  const isUnnamedEntrySentinel =
    field.type === FieldType.ENTRY_LINK && firstTextValue.startsWith('Unnamed entry:');

  if (firstTextValue && !isUnnamedEntrySentinel) {
    return { kind: 'value', text: firstTextValue };
  }

  if (value.type === FieldType.PAGE_LINK) {
    const firstPageLink = filterValidPageLinkValue(value.value)[0];
    const fallbackUrl =
      firstPageLink && 'contentId' in firstPageLink && firstPageLink.contentId
        ? getPageLinkUrl(firstPageLink.contentId, baseUrl)
        : undefined;

    return {
      kind: 'value',
      text: resolvedPreviewValues[entry.id]?.[field.id] ?? fallbackUrl ?? field.name
    };
  }

  if (value.type === FieldType.ENTRY_LINK) {
    const firstEntryTitle = value.value.find(
      (entryReference) =>
        'entryId' in entryReference &&
        'title' in entryReference &&
        typeof entryReference.title === 'string' &&
        entryReference.title.trim()
    );

    return {
      kind: 'value',
      text:
        firstEntryTitle && 'title' in firstEntryTitle
          ? String(firstEntryTitle.title)
          : unnamedEntryLabel
    };
  }

  return { kind: 'value', text: field.name };
};

export const getAiSummaryPreviewOptions = ({
  database,
  state,
  locale,
  blankLabel,
  checkedLabel,
  uncheckedLabel,
  baseUrl = '',
  resolvedPreviewValues = {},
  unnamedEntryLabel = 'Unnamed entry'
}: {
  database: Database;
  state: DefaultRootState | undefined;
  locale: string;
  blankLabel: string;
  checkedLabel: string;
  uncheckedLabel: string;
  baseUrl?: string;
  resolvedPreviewValues?: ResolvedPreviewValues;
  unnamedEntryLabel?: string;
}): AiSummaryPreviewOption[] => {
  const entries = getPreviewEntries(database);
  const [primaryField, descriptionField] = getPreviewFields(database);

  return entries.map((entry, entryIndex) => {
    const primaryValue = serializePreviewValue({
      database,
      entry,
      field: primaryField,
      state,
      locale,
      baseUrl,
      resolvedPreviewValues,
      unnamedEntryLabel,
      checkedLabel,
      uncheckedLabel
    });
    const descriptionValue = descriptionField
      ? serializePreviewValue({
          database,
          entry,
          field: descriptionField,
          state,
          locale,
          baseUrl,
          resolvedPreviewValues,
          unnamedEntryLabel,
          checkedLabel,
          uncheckedLabel
        })
      : undefined;
    const description =
      descriptionField && descriptionValue
        ? `${descriptionField.name}: ${
            descriptionValue.kind === 'empty' ? blankLabel : descriptionValue.text
          }`
        : undefined;

    return {
      label: primaryValue.kind === 'empty' ? blankLabel : primaryValue.text,
      ...(description ? { description } : {}),
      entryId: entry.id,
      value: entryIndex + 1
    };
  });
};

export const loadAiSummaryPreviewDependencies = ({
  contentId,
  database,
  helpers,
  intl,
  loadValueDependencies = loadDependencies,
  state
}: {
  contentId: string;
  database: Database;
  helpers: Pick<LoadDependenciesHelpers, 'dispatch' | 'selector'>;
  intl: IntlShape;
  loadValueDependencies?: typeof loadDependencies;
  state: DefaultRootState | undefined;
}): void => {
  const entries = getPreviewEntries(database);
  const fields = getPreviewFields(database);

  for (const field of fields) {
    if (field.type !== FieldType.PAGE_LINK) {
      for (const entry of entries) {
        const value = getConvertedDatabaseValue(database, field, entry, state);
        loadValueDependencies(
          {
            contentId,
            entryId: entry.id,
            field,
            value
          },
          {
            ...helpers,
            intl,
            selectDatabase: selectDatabaseDraft
          },
          state
        );
      }
    }
  }
};

const useLoadAiSummaryPreviewDependencies = ({
  contentId,
  intl,
  isEnabled
}: {
  contentId: string;
  intl: IntlShape;
  isEnabled: boolean;
}): void => {
  const store = defaultStoreUtils.useStore();

  useActionWithDependencies(
    useCallback(
      (helpers) => {
        if (!isEnabled) {
          return;
        }

        const database = helpers.selector((state) =>
          selectDatabaseDraft(state, { contentId })
        );
        if (!database || isBrokenDatabase(database)) {
          return;
        }

        const state = store.getState();
        loadAiSummaryPreviewDependencies({
          contentId,
          database,
          helpers,
          intl,
          state
        });
      },
      [contentId, intl, isEnabled, store]
    )
  );
};

export const useAiSummaryPreviewOptions = (
  contentId: string,
  isEnabled: boolean
): AiSummaryPreviewOption[] => {
  const locale = useLocale();
  const intl = useIntl();
  const blankLabel = intl.formatMessage(
    messages['field-settings.ai-summary.preview-blank']
  );
  const checkedLabel = intl.formatMessage(
    messages['field-settings.ai-summary.preview-checked']
  );
  const uncheckedLabel = intl.formatMessage(
    messages['field-settings.ai-summary.preview-unchecked']
  );
  const unnamedEntryLabel = intl.formatMessage(
    messages['unnamed-entities.unnamed-entry']
  );
  const database = defaultStoreUtils.useEqualSelector((state) =>
    isEnabled
      ? selectDatabaseDraftByContentId(state, {
          contentId,
          includeDeleted: false
        })
      : undefined
  );
  const [resolvedPreviewValues, setResolvedPreviewValues] =
    useState<ResolvedPreviewValues>({});

  useLoadAiSummaryPreviewDependencies({ contentId, intl, isEnabled });

  useEffect(() => {
    if (!isEnabled || !database || isBrokenDatabase(database)) {
      setResolvedPreviewValues({});
      return;
    }

    let isCancelled = false;
    const smartLinkClient = getService('SmartLinkClient');

    void resolvePageLinkPreviewValues({
      baseUrl: getBaseUrl(),
      database,
      smartLinkClient
    })
      .then((values) => {
        if (!isCancelled) {
          setResolvedPreviewValues(values);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setResolvedPreviewValues({});
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [database, isEnabled]);

  return defaultStoreUtils.useEqualSelector((state) => {
    if (!isEnabled || !database) {
      return EMPTY_PREVIEW_OPTIONS;
    }

    return getAiSummaryPreviewOptions({
      database,
      state,
      locale,
      blankLabel,
      checkedLabel,
      uncheckedLabel,
      baseUrl: getBaseUrl(),
      resolvedPreviewValues,
      unnamedEntryLabel
    });
  });
};
